import { region, upload } from 'qiniu-js';
import {
  completeBossMediaDirectUpload,
  initBossMediaDirectUpload,
} from '@/services/core/bossmedia';

export interface UploadedImage {
  url: string;
  mediaId?: string;
  name?: string;
  uploadId?: string;
  mediaInfo?: CoreAPI.MultiMediaVO;
}

export interface UploadImageOptions {
  mimeType?: string;
  onProgress?: (percent: number) => void;
}

export interface ImageUploadTask {
  promise: Promise<UploadedImage>;
  abort: () => void;
}

interface DirectUploadInitData {
  uploadId?: string;
  objectKey?: string;
  uploadToken?: string;
  region?: string;
}

interface CommonApiResponse<T> {
  success?: boolean;
  data?: T;
  errorMessage?: string;
  uniformErrorMsg?: string;
}

const IMAGE_MIME_BY_EXTENSION: Record<string, string> = {
  gif: 'image/gif',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
};

export class UploadAbortError extends Error {
  constructor() {
    super('图片上传已取消');
    this.name = 'UploadAbortError';
  }
}

export const isUploadAbortError = (error: unknown) =>
  error instanceof UploadAbortError ||
  (error instanceof DOMException && error.name === 'AbortError');

const assertResponse = <T>(
  response: CommonApiResponse<T>,
  fallbackMessage: string,
) => {
  if (response.success === false || !response.data) {
    throw new Error(
      response.errorMessage || response.uniformErrorMsg || fallbackMessage,
    );
  }
  return response.data;
};

const resolveImageMimeType = (file: File, overrideMimeType?: string) => {
  const browserMime = (overrideMimeType || file.type).trim().toLowerCase();
  if (browserMime) {
    return browserMime;
  }
  const extension = file.name.split('.').pop()?.toLowerCase() || '';
  return IMAGE_MIME_BY_EXTENSION[extension] || '';
};

const resolveQiniuRegion = (regionName?: string) => {
  switch (regionName) {
    case region.z0:
      return region.z0;
    case region.z1:
      return region.z1;
    case region.z2:
      return region.z2;
    case region.na0:
      return region.na0;
    case region.as0:
      return region.as0;
    case region.cnEast2:
      return region.cnEast2;
    default:
      return undefined;
  }
};

const toUploadedImage = (
  mediaInfo: CoreAPI.MultiMediaVO,
  uploadId: string,
): UploadedImage => {
  const url = mediaInfo.visitUrl?.trim();
  if (!url) {
    throw new Error('后端未返回图片访问地址');
  }
  return {
    url,
    mediaId:
      typeof mediaInfo.id === 'undefined' ? undefined : String(mediaInfo.id),
    name: mediaInfo.originalFilename || mediaInfo.mediaName,
    uploadId,
    mediaInfo,
  };
};

export const createImageUpload = (
  file: File,
  options: UploadImageOptions = {},
): ImageUploadTask => {
  let aborted = false;
  let unsubscribe: (() => void) | undefined;
  let rejectActiveUpload: ((reason: UploadAbortError) => void) | undefined;
  const abortController = new AbortController();

  const assertNotAborted = () => {
    if (aborted) {
      throw new UploadAbortError();
    }
  };

  const promise = (async () => {
    const mimeType = resolveImageMimeType(file, options.mimeType);
    const initData = assertResponse<DirectUploadInitData>(
      (await initBossMediaDirectUpload(
        {
          fileName: file.name,
          fileSize: file.size,
          mimeType,
          fileCategory: 'IMAGE',
        },
        { signal: abortController.signal },
      )) as CommonApiResponse<DirectUploadInitData>,
      '初始化图片上传失败',
    );
    const { uploadId, objectKey, uploadToken } = initData;
    if (!uploadId || !objectKey || !uploadToken) {
      throw new Error('后端返回的直传凭证不完整');
    }
    assertNotAborted();

    await new Promise<void>((resolve, reject) => {
      rejectActiveUpload = reject;
      const observable = upload(
        file,
        objectKey,
        uploadToken,
        { fname: file.name, mimeType },
        {
          region: resolveQiniuRegion(initData.region),
          useCdnDomain: true,
        },
      );
      const subscription = observable.subscribe({
        next: (progress) => {
          options.onProgress?.(Math.min(99, progress.total.percent));
        },
        error: (error) => reject(error),
        complete: () => resolve(),
      });
      unsubscribe = () => subscription.unsubscribe();
      if (aborted) {
        unsubscribe();
        reject(new UploadAbortError());
      }
    });
    rejectActiveUpload = undefined;
    assertNotAborted();

    const mediaInfo = assertResponse<CoreAPI.MultiMediaVO>(
      (await completeBossMediaDirectUpload(
        { uploadId },
        { signal: abortController.signal },
      )) as CommonApiResponse<CoreAPI.MultiMediaVO>,
      '确认图片上传失败',
    );
    options.onProgress?.(100);
    return toUploadedImage(mediaInfo, uploadId);
  })();

  return {
    promise,
    abort: () => {
      if (aborted) {
        return;
      }
      aborted = true;
      abortController.abort();
      unsubscribe?.();
      rejectActiveUpload?.(new UploadAbortError());
    },
  };
};

export const uploadImageFile = (file: File, options: UploadImageOptions = {}) =>
  createImageUpload(file, options).promise;
